import { Bot, MessageSquare, GitBranch, Play, CheckCircle, Users, Lightbulb, Edit3, Sparkles, Inbox, Upload, Filter, Globe } from 'lucide-react';
import { StepType } from '../types';

export interface StepTypeConfiguration {
  name: string;
  description: string;
  icon: any;
  color: string;
  borderColor: string;
  hoverColor: string;
  deprecated?: boolean;
}

// Step types configuration
export const stepTypeConfig: Record<string, StepTypeConfiguration> = {
  run_agent: {
    name: 'Agent',
    description: 'Run an AI agent with flexible capabilities',
    icon: Bot,
    color: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
    borderColor: 'border-indigo-200 dark:border-indigo-800',
    hoverColor: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/40',
  },
  assistant: {
    name: 'Assistant',
    description: 'Interactive AI assistant with conversation history',
    icon: Users,
    color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
    borderColor: 'border-teal-200 dark:border-teal-800',
    hoverColor: 'hover:bg-teal-100 dark:hover:bg-teal-900/40',
  },
  user_prompt: {
    name: 'User Prompt',
    description: 'Get input from the user',
    icon: MessageSquare,
    color: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
    borderColor: 'border-orange-200 dark:border-orange-800',
    hoverColor: 'hover:bg-orange-100 dark:hover:bg-orange-900/40',
  },
  conditional: {
    name: 'Conditional',
    description: 'Create branching logic based on conditions',
    icon: GitBranch,
    color: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    hoverColor: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/40',
  },
  filter: {
    name: 'Filter',
    description: 'Keep only list elements that match a per-element predicate',
    icon: Filter,
    // Rose/red palette distinguishes Filter from Conditional yellow (AC-1273).
    color: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300',
    borderColor: 'border-rose-200 dark:border-rose-800',
    hoverColor: 'hover:bg-rose-100 dark:hover:bg-rose-900/40',
  },
  auto_prompt: {
    name: 'Auto Prompt',
    description: 'Automatically generate prompt data without user interaction',
    icon: Sparkles,
    color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    borderColor: 'border-purple-200 dark:border-purple-800',
    hoverColor: 'hover:bg-purple-100 dark:hover:bg-purple-900/40',
  },
  queue_read: {
    name: 'Queue Read',
    description: 'Read data from a queue to initiate orchestration',
    icon: Inbox,
    color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
    borderColor: 'border-purple-200 dark:border-purple-800',
    hoverColor: 'hover:bg-purple-100 dark:hover:bg-purple-900/40',
  },
  queue_write: {
    name: 'Queue Write',
    description: 'Write workflow output to a queue',
    icon: Upload,
    color: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
    borderColor: 'border-teal-200 dark:border-teal-800',
    hoverColor: 'hover:bg-teal-100 dark:hover:bg-teal-900/40',
  },
  // AC-1444 (epic AC-1434): Domain Availability (RDAP) data-flow step. Grouped
  // alongside filter / queue_write per TD section 3.4. Blue palette + Globe
  // icon (matches RDAPLookupNode badge) keeps it visually distinct from
  // filter (rose) and queue_write (teal) in the palette.
  rdap_lookup: {
    name: 'Domain Availability',
    description: 'Check whether domains are available via RDAP (no API key required)',
    icon: Globe,
    color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-200 dark:border-blue-800',
    hoverColor: 'hover:bg-blue-100 dark:hover:bg-blue-900/40',
  },
};

/**
 * Get configuration for a step type
 * @param stepType - The step type to get configuration for
 * @returns Step type configuration or null if not found
 */
export function getStepTypeConfig(stepType: StepType): StepTypeConfiguration | null {
  return stepTypeConfig[stepType] || null;
}

/**
 * Get all available step types
 * @returns Array of step type configurations
 */
export function getAllStepTypes(): StepType[] {
  return Object.keys(stepTypeConfig) as StepType[];
}